import { format } from 'date-fns';
import { useState } from 'react';

import { CashFlowConfig } from '@ndvr/portfolio-api';

import { cn } from '@/utils/cn';
import { formatCurrency } from 'utils';

import {
    ChartWindow,
    compactAmount,
    defaultWindow,
    formatWindow,
    monthIndex,
    monthlyWithdrawals,
} from './policy-cashflow-chart';
import { PolicyChartWindowPicker } from './PolicyChartWindowPicker';

const BAR = '#D45A4E';
// Bars use at most this share of the plot, leaving room for the amount label over the tallest one.
const PLOT_SHARE = 86;

interface PolicyCashFlowChartProps {
    withdrawals: CashFlowConfig[];
    today: Date;
    /** Overrides the opening window, for stories. */
    initialRange?: ChartWindow;
}

/** Scheduled withdrawals per month. */
export const PolicyCashFlowChart = ({ withdrawals, today, initialRange }: PolicyCashFlowChartProps) => {
    const [range, setRange] = useState<ChartWindow>(() => initialRange ?? defaultWindow(withdrawals, today));
    const [hovered, setHovered] = useState<number>();
    const months = monthlyWithdrawals(withdrawals, range);
    const peak = Math.max(1, ...months.map((month) => month.total));
    const total = months.reduce((sum, month) => sum + month.total, 0);
    const dense = range.months > 12;
    const hover = hovered === undefined ? undefined : months[hovered];

    return (
        <section
            aria-label="Monthly cash flow"
            className="flex flex-col gap-3 rounded-lg border border-[#CECAC6] px-[18px] pt-4 pb-3"
        >
            <div className="flex min-h-8 flex-wrap items-center gap-x-3.5 gap-y-2">
                <span className="flex flex-1 items-center gap-2">
                    <span className="text-[11px] font-semibold tracking-[0.05em] text-[#897E7B] uppercase">
                        Monthly cash flow
                    </span>
                    {hover && (
                        <span className="rounded-[4px] bg-[#F1EFEC] px-2 text-[11.5px] leading-[18px] font-medium text-[#2C2424]">
                            {format(hover.month, 'MMM yyyy')} · {formatCurrency(hover.total)}
                        </span>
                    )}
                </span>
                <PolicyChartWindowPicker value={range} onChange={setRange} today={today} />
            </div>
            <div
                role="img"
                aria-label={`Monthly withdrawals, ${formatWindow(range)}: ${formatCurrency(total)} in withdrawals`}
                className="relative flex h-60 flex-col"
                onMouseLeave={() => setHovered(undefined)}
            >
                <div className="relative flex min-h-0 flex-1">
                    <div
                        aria-hidden="true"
                        className={cn('pointer-events-none absolute inset-0 flex', dense ? 'gap-[3px]' : 'gap-2')}
                    >
                        {months.map((month, index) => (
                            <div key={month.month.getTime()} className="flex h-full min-w-0 flex-1 justify-center">
                                <span
                                    className={cn(
                                        'block h-full w-px',
                                        hovered === index ? 'bg-[#2C2424]' : 'bg-[#EEEBE8]',
                                    )}
                                />
                            </div>
                        ))}
                    </div>
                    <div className={cn('absolute inset-0 z-[2] flex', dense ? 'gap-[3px]' : 'gap-2')}>
                        {months.map((month, index) => (
                            <div
                                key={month.month.getTime()}
                                className={cn(
                                    'h-full min-w-0 flex-1 rounded-md',
                                    hovered === index && 'bg-[rgba(44,36,36,0.05)]',
                                )}
                                onMouseEnter={() => setHovered(index)}
                            />
                        ))}
                    </div>
                    {total === 0 && (
                        <span className="absolute inset-0 z-[1] flex items-center justify-center text-[12.5px] text-[#897E7B]">
                            No withdrawals in this period
                        </span>
                    )}
                    <div className={cn('relative z-[1] flex flex-1 items-end', dense ? 'gap-[3px]' : 'gap-2')}>
                        {months.map((month) => (
                            <div
                                key={month.month.getTime()}
                                className="flex h-full min-w-0 flex-1 flex-col items-stretch justify-end"
                            >
                                {month.total > 0 && !dense && (
                                    <span className="pb-1 text-center text-[10.5px] whitespace-nowrap text-[#897E7B] tabular-nums">
                                        {compactAmount(month.total)}
                                    </span>
                                )}
                                <span
                                    className="block rounded-t-[4px]"
                                    style={{ height: `${(month.total / peak) * PLOT_SHARE}%`, background: BAR }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <div className="relative z-[1] h-px shrink-0 bg-[#897E7B]" />
                <div className={cn('flex', dense ? 'gap-[3px]' : 'gap-2')}>
                    {months.map((month, index) => {
                        const january = month.month.getMonth() === 0;
                        const label = format(month.month, january || index === 0 ? "MMM ''yy" : 'MMM');
                        const current = monthIndex(month.month) === monthIndex(today);
                        const visible = !dense || hovered === index || month.month.getMonth() % 3 === 0 || index === 0;
                        return (
                            <span
                                key={month.month.getTime()}
                                className="flex min-w-0 flex-1 flex-col items-center gap-[3px]"
                            >
                                <span
                                    className={cn(
                                        'block h-1.5 w-px',
                                        hovered === index ? 'bg-[#2C2424]' : 'bg-[#CECAC6]',
                                    )}
                                />
                                <span
                                    className={cn(
                                        'text-center whitespace-nowrap',
                                        dense ? 'text-[10px]' : 'text-[11px]',
                                        current || hovered === index
                                            ? 'font-semibold text-[#0D0D0D]'
                                            : 'text-[#897E7B]',
                                    )}
                                >
                                    {visible ? label : ''}
                                </span>
                            </span>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
